import React from 'react';
import { BaseForm, BaseFormInterface, BaseFormProps } from '@app/components/common/forms//BaseForm/BaseForm';
import { BaseButtonsGroup } from '@app/components/common/forms/components/BaseButtonsGroup/BaseButtonsGroup';
import { BaseFormTitle } from '@app/components/common/forms/components/BaseFormTitle/BaseFormTitle';
import { BaseFormItem } from '@app/components/common/forms/components/BaseFormItem/BaseFormItem';
import { BaseFormList } from '@app/components/common/forms/components/BaseFormList/BaseFormList';
import styled, { css } from 'styled-components';
import { useAppSelector } from '@app/hooks/reduxHooks';

export interface BaseButtonsFormProps extends BaseFormProps {
  isFieldsChanged: boolean;
  setFieldsChanged?: (state: boolean) => void;
  footer?: React.ReactElement;
  loading?: boolean;
  buttonSize?: 'small' | 'middle' | 'large';
  onCancel?: () => void;
}

const StyledBaseForm = styled(BaseForm)`
  ${(props) => {
    const color = props.theme === 'dark' ? '#d6f52a' : '#ff6a00';
    const shadow = props.theme === 'dark' ? '#d6f52a1c' : '#f5a22a1c';
    return css`
      .ant-input-focused,
      .ant-input:focus {
        border-color: ${color};
        box-shadow: 0 0 0 2px ${shadow};
      }
      .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
        border-color: ${color};
        box-shadow: 0 0 0 2px ${shadow};
      }
      .ant-input-number:hover {
        border-color: ${color};
      }
      .ant-picker-focused,
      .ant-picker:hover {
        border-color: ${color};
      }
      .ant-picker-focused {
        box-shadow: 0 0 0 2px ${shadow};
      }
      .ant-radio-input:focus + .ant-radio-inner {
        box-shadow: 0 0 0 3px ${shadow};
      }
    `;
  }}
`;

export const BaseButtonsForm: BaseFormInterface<BaseButtonsFormProps> = ({
  form,
  isFieldsChanged,
  setFieldsChanged,
  footer,
  loading = false,
  buttonSize,
  onCancel: onCancelProp,
  children,
  ...props
}) => {
  const [formDefault] = BaseForm.useForm();
  const currentForm = form || formDefault;
  const theme = useAppSelector((state) => state.theme.theme);
  const handleCancel = () => {
    if (onCancelProp) {
      onCancelProp();
    } else {
      currentForm?.resetFields();
      setFieldsChanged && setFieldsChanged(false);
    }
  };

  return (
    <StyledBaseForm form={currentForm} {...props} theme={theme}>
      {children}
      {isFieldsChanged &&
        (footer || <BaseButtonsGroup loading={loading} onCancel={handleCancel} size={buttonSize} />)}
    </StyledBaseForm>
  );
};

BaseButtonsForm.Title = BaseFormTitle;
BaseButtonsForm.Item = BaseFormItem;
BaseButtonsForm.List = BaseFormList;
BaseButtonsForm.useForm = BaseForm.useForm;
BaseButtonsForm.Provider = BaseForm.Provider;
